<template>
  <div data-testid="metronome_config">
    <template v-if="showSpeedControls">
      <div v-if="!clinicMode" class="subtitle-1 mb-2" data-testid="metronome_config_title">
        {{ $t("common.metronome") }}
      </div>
      <v-layout align-center>
        <v-flex xs2 class="text-left">
          <span class="headline">{{ value.speed }}</span>
          <span class="caption ml-1">{{ $t("common.bpm") }}</span>
        </v-flex>
        <v-flex :xs8="clinicMode" :xs10="!clinicMode">
          <vue-slider
            v-model="speed"
            :min="speedMinimum"
            :max="speedMaximum"
            :interval="1"
            tooltip="none"
            data-testid="metronome_config_speed"
          />
        </v-flex>
        <v-flex v-if="clinicMode" xs2 class="text-right">
          <v-btn
            :loading="previewLoading"
            data-testid="metronome_config_preview"
            @click="togglePreview"
          >
            <v-icon left>{{ previewing ? "stop" : "play_arrow" }}</v-icon>
            {{ previewing ? $t("common.stop") : $t("common.preview") }}
          </v-btn>
        </v-flex>
      </v-layout>
    </template>

    <v-layout
      v-if="clinicMode || value.allow_customized_sound"
      align-center
      class="mt-3"
    >
      <v-flex xs12 :md3="clinicMode" :md4="!clinicMode">
        <v-select
          v-model="soundTick"
          :items="soundOptions"
          :label="$t('metronome_config.tick_sound')"
          dense
          outlined
          data-testid="metronome_config_sound_tick"
          @change="handleTickSoundChange"
        />
      </v-flex>
      <v-flex v-if="showTockSound" xs12 :md3="clinicMode" :md4="!clinicMode" class="pl-md-4">
        <v-select
          v-model="soundTock"
          :items="tockSoundOptions"
          :label="$t('metronome_config.tock_sound')"
          dense
          outlined
          data-testid="metronome_config_sound_tock"
          @change="handleTockSoundChange"
        />
      </v-flex>
      <v-flex
        v-if="showTockEvery"
        xs12
        :md3="clinicMode"
        :md4="!clinicMode"
        class="pl-md-4"
      >
        <v-text-field
          v-model="tockEvery"
          :label="$t('metronome_config.tock_every')"
          :suffix="$t('metronome_config.beats')"
          type="number"
          min="1"
          max="100"
          step="1"
          dense
          outlined
          data-testid="metronome_config_tock_every"
        />
      </v-flex>
      <v-flex v-if="clinicMode" xs12 md3 class="pl-md-4">
        <v-switch
          v-model="peripheral"
          :label="$t('metronome_config.peripheral_cue')"
          class="mt-n7"
          hide-details
          data-testid="metronome_config_peripheral"
        />
      </v-flex>
    </v-layout>

    <v-alert v-if="previewError" dense type="error" class="mt-3">
      {{ $t("metronome_config.preview_error") }}
    </v-alert>

    <v-layout v-if="clinicMode && allowPatientCustomization" align-center class="mt-2 mb-6" justify-space-between>
      <v-flex shrink>
        <v-switch
          v-model="allowCustomizedBpm"
          :label="$t('metronome_config.allow_patient_customization')"
          class="mt-0"
          hide-details
          data-testid="metronome_config_allow_patient_customization"
        />
      </v-flex>
      <v-flex v-if="value.allow_customized_bpm" shrink class="pl-12">
        <v-switch
          v-model="allowCustomizedSound"
          :label="$t('metronome_config.allow_customized_sound')"
          class="mt-0"
          hide-details
          data-testid="metronome_config_allow_customized_sound"
        />
      </v-flex>
      <v-flex v-if="value.allow_customized_bpm" class="pl-12">
        <div class="caption mb-1">{{ $t("metronome_config.patient_bpm_range") }}</div>
        <vue-slider
          v-model="bpmRange"
          :min="minimumBpm"
          :max="maximumBpm"
          :interval="1"
          tooltip="always"
          data-testid="metronome_config_bpm_range"
        />
      </v-flex>
    </v-layout>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import VueSlider from "vue-slider-component";
import "vue-slider-component/theme/default.css";
import {
  METRONOME_MAX_BPM,
  METRONOME_MIN_BPM,
  MetronomeConfig as MetronomeConfigValue,
  MetronomeConfigValidationError,
  MetronomeSound,
  MetronomeTockSound,
  normalizeMetronomeConfig,
  validateMetronomeConfig,
} from "../lib/metronome_config";
import { MetronomePreview } from "../lib/metronome_preview";

export default Vue.extend({
  name: "MetronomeConfig",
  components: { VueSlider },
  props: {
    value: {
      type: Object as () => MetronomeConfigValue,
      required: true,
    },
    mode: {
      type: String,
      default: "clinic",
      validator: (value: string) => ["clinic", "patient"].includes(value),
    },
    allowPatientCustomization: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      minimumBpm: METRONOME_MIN_BPM,
      maximumBpm: METRONOME_MAX_BPM,
      preview: new MetronomePreview(),
      previewing: false,
      previewLoading: false,
      previewError: false,
    };
  },
  computed: {
    clinicMode(): boolean {
      return this.mode === "clinic";
    },
    showSpeedControls(): boolean {
      if (this.clinicMode) return true;
      if (!this.value.allow_customized_bpm) return false;
      return this.value.bpm_range.min !== this.value.bpm_range.max;
    },
    showTockSound(): boolean {
      return this.clinicMode || this.value.sound_tock !== "none";
    },
    showTockEvery(): boolean {
      return this.clinicMode && this.value.sound_tock !== "none";
    },
    speedMinimum(): number {
      return this.clinicMode ? METRONOME_MIN_BPM : this.value.bpm_range.min;
    },
    speedMaximum(): number {
      return this.clinicMode ? METRONOME_MAX_BPM : this.value.bpm_range.max;
    },
    speed: {
      get(): number {
        return this.value.speed;
      },
      set(speed: number): void {
        this.updateConfig({ speed });
      },
    },
    soundTick: {
      get(): string {
        return this.value.sound_tick;
      },
      set(sound_tick: MetronomeSound): void {
        this.updateConfig({ sound_tick });
      },
    },
    soundTock: {
      get(): string {
        return this.value.sound_tock;
      },
      set(sound_tock: MetronomeTockSound): void {
        this.updateConfig({ sound_tock });
      },
    },
    tockEvery: {
      get(): number {
        return this.value.tock_every;
      },
      set(tock_every: number | string): void {
        this.updateConfig({ tock_every: Number(tock_every) });
      },
    },
    peripheral: {
      get(): boolean {
        return this.value.peripheral;
      },
      set(peripheral: boolean): void {
        this.updateConfig({ peripheral });
      },
    },
    allowCustomizedBpm: {
      get(): boolean {
        return this.value.allow_customized_bpm;
      },
      set(allow_customized_bpm: boolean): void {
        this.updateConfig({
          allow_customized_bpm,
          ...(!allow_customized_bpm ? { allow_customized_sound: false } : {}),
        });
      },
    },
    allowCustomizedSound: {
      get(): boolean {
        return this.value.allow_customized_sound;
      },
      set(allow_customized_sound: boolean): void {
        this.updateConfig({ allow_customized_sound });
      },
    },
    bpmRange: {
      get(): number[] {
        return [this.value.bpm_range.min, this.value.bpm_range.max];
      },
      set([min, max]: number[]): void {
        this.updateConfig({ bpm_range: { min, max } });
      },
    },
    soundOptions(): Array<{ value: string; text: string }> {
      return [
        { value: "metronome", text: this.$t("common.metronome") as string },
        { value: "drumstick", text: this.$t("tool_metronome.drum_sticks") as string },
        { value: "tincan", text: this.$t("tool_metronome.tin_can") as string },
        { value: "woodblock", text: this.$t("tool_metronome.wood_block") as string },
      ];
    },
    tockSoundOptions(): Array<{ value: string; text: string }> {
      if (!this.clinicMode) return this.soundOptions;
      return [
        ...this.soundOptions,
        { value: "none", text: this.$t("common.none") as string },
      ];
    },
  },
  watch: {
    "value.speed"(): void {
      this.restartPreview(false);
    },
    "value.tock_every"(): void {
      this.updatePreviewPattern(
        this.value.sound_tick,
        this.value.sound_tock
      );
    },
  },
  mounted() {
    const normalized = normalizeMetronomeConfig(this.value);
    if (JSON.stringify(normalized) !== JSON.stringify(this.value)) {
      this.$emit("input", normalized);
    }
  },
  beforeDestroy() {
    void this.preview.dispose();
  },
  methods: {
    updateConfig(patch: Partial<MetronomeConfigValue>): void {
      this.$emit(
        "input",
        normalizeMetronomeConfig({
          ...this.value,
          ...patch,
          bpm_range: patch.bpm_range || this.value.bpm_range,
        })
      );
    },
    async togglePreview(): Promise<void> {
      this.previewError = false;
      if (this.previewing) {
        this.preview.stop();
        this.previewing = false;
        return;
      }

      this.previewLoading = true;
      try {
        await this.preview.start(
          this.value.speed,
          this.value.sound_tick,
          this.value.sound_tock,
          this.value.tock_every
        );
        this.previewing = this.preview.playing;
      } catch (_error) {
        this.previewError = true;
        this.previewing = false;
      } finally {
        this.previewLoading = false;
      }
    },
    async restartPreview(playImmediately = true): Promise<void> {
      if (!this.previewing) return;
      try {
        await this.preview.start(
          this.value.speed,
          this.value.sound_tick,
          this.value.sound_tock,
          this.value.tock_every,
          playImmediately
        );
        this.previewing = this.preview.playing;
      } catch (_error) {
        this.previewError = true;
        this.previewing = false;
      }
    },
    async handleTickSoundChange(sound: MetronomeSound): Promise<void> {
      await this.handleSoundChange(sound, this.value.sound_tock, sound);
    },
    async handleTockSoundChange(sound: MetronomeTockSound): Promise<void> {
      await this.handleSoundChange(this.value.sound_tick, sound, sound);
    },
    async handleSoundChange(
      soundTick: MetronomeSound,
      soundTock: MetronomeTockSound,
      selectedSound: MetronomeTockSound
    ): Promise<void> {
      this.previewError = false;
      if (this.previewing) {
        await this.updatePreviewPattern(soundTick, soundTock);
        return;
      }
      if (selectedSound === "none") return;
      try {
        await this.preview.playOnce(selectedSound);
      } catch (_error) {
        this.previewError = true;
      }
    },
    async updatePreviewPattern(
      soundTick: MetronomeSound,
      soundTock: MetronomeTockSound
    ): Promise<void> {
      if (!this.previewing) return;
      try {
        await this.preview.updatePattern(
          soundTick,
          soundTock,
          this.value.tock_every
        );
      } catch (_error) {
        this.previewError = true;
      }
    },
    validate(
      value: MetronomeConfigValue = this.value
    ): string[] {
      const translationKeys: Record<MetronomeConfigValidationError, string> = {
        invalid_speed: "metronome_config.invalid_speed",
        invalid_sound: "metronome_config.invalid_sound",
        invalid_tock_every: "metronome_config.invalid_tock_every",
        invalid_bpm_range: "metronome_config.invalid_bpm_range",
        speed_outside_bpm_range: "metronome_config.speed_outside_bpm_range",
      };
      return validateMetronomeConfig(value).map((error) =>
        this.$t(translationKeys[error]) as string
      );
    },
  },
});
</script>
